import React, { useEffect, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ESIGENZE } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  nome: z.string().min(2, "Inserisci il tuo nome"),
  azienda: z.string().optional().or(z.literal("")),
  ruolo: z.string().optional().or(z.literal("")),
  email: z.string().email("Email non valida"),
  telefono: z.string().optional().or(z.literal("")),
  esigenza: z.string().optional().or(z.literal("")),
  messaggio: z.string().min(10, "Raccontaci qualcosa in più (min 10 caratteri)"),
  // Honeypot — non visibile a un essere umano. Se compilato → bot.
  honeypot: z.string().optional().or(z.literal("")),
});

const ContactForm = ({ tone = "dark", defaultSubject = "", compact = false }) => {
  void compact; // reserved for future style variants — kept for API compatibility
  const isDark = tone === "dark"; // dark = on light bg
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("idle"); // 'idle' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState("");
  const [sourcePage, setSourcePage] = useState("");
  const [utm, setUtm] = useState({ source: "", medium: "", campaign: "" });

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Page URL without query/fragment (avoid leaking sensitive params to Pabbly)
    setSourcePage(window.location.origin + window.location.pathname);
    // Extract UTM parameters from current URL (if any)
    try {
      const q = new URLSearchParams(window.location.search);
      setUtm({
        source: q.get("utm_source") || "",
        medium: q.get("utm_medium") || "",
        campaign: q.get("utm_campaign") || "",
      });
    } catch (_) {
      // noop — URLSearchParams always available in modern browsers
    }
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: defaultSubject
      ? { messaggio: `Oggetto: ${defaultSubject}\n\n` }
      : undefined,
  });

  const onSubmit = async (values) => {
    if (submitting) return; // defensive: prevent double submit via Enter key
    setSubmitting(true);
    setStatus("idle");
    setErrorMsg("");
    try {
      const payload = {
        nome: values.nome,
        email: values.email,
        messaggio: values.messaggio,
        azienda: values.azienda || null,
        ruolo: values.ruolo || null,
        telefono: values.telefono || null,
        esigenza: values.esigenza || defaultSubject || null,
        source_page: sourcePage || null,
        honeypot: values.honeypot || "",
        gdpr_consent: true,
        utm_source: utm.source || null,
        utm_medium: utm.medium || null,
        utm_campaign: utm.campaign || null,
      };
      const res = await axios.post(`${API}/contact`, payload, { timeout: 15000 });
      if (!res || !res.data || res.data.success !== true) {
        throw new Error("Errore di invio");
      }
      setStatus("success");
      reset(
        defaultSubject
          ? { messaggio: `Oggetto: ${defaultSubject}\n\n` }
          : { messaggio: "" }
      );
    } catch (e) {
      setStatus("error");
      // Only surface backend-provided error detail — never raw exception content
      const backendDetail =
        e && e.response && e.response.data && e.response.data.detail;
      setErrorMsg(
        backendDetail ||
          "Non siamo riusciti a inviare la richiesta. Riprova tra qualche minuto o scrivici direttamente a hello@weareonsports.com"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${
        isDark ? "text-dodgers" : "text-galactico"
      }`}
      noValidate
      data-testid="contact-form"
    >
      {/* Honeypot anti-spam — hardened contro autofill browser e password manager.
          IMPORTANTE: {...register("honeypot")} è spreaded PRIMA, poi gli attributi
          vengono SOVRASCRITTI dopo (name, autoComplete, readOnly). Il backend
          continua a ricevere il campo 'honeypot' perché react-hook-form raccoglie
          i valori per chiave di registrazione (non per attributo DOM name). */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: 0,
          height: 0,
          overflow: "hidden",
        }}
      >
        <label htmlFor="hp-xf-field">Non compilare questo campo</label>
        <input
          {...register("honeypot")}
          id="hp-xf-field"
          name="website_url_x"
          type="text"
          tabIndex={-1}
          readOnly
          autoComplete="off"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-testid="contact-honeypot"
        />
      </div>

      <div>
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Nome e cognome *
        </label>
        <input
          className="input-os"
          placeholder="Mario Rossi"
          autoComplete="name"
          {...register("nome")}
          data-testid="contact-nome"
        />
        {errors.nome && <p className="text-rosso-enzo text-sm mt-1">{errors.nome.message}</p>}
      </div>
      <div>
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Azienda
        </label>
        <input className="input-os" placeholder="Es. Acme Sport" autoComplete="organization" {...register("azienda")} data-testid="contact-azienda" />
      </div>
      <div>
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Ruolo
        </label>
        <input className="input-os" placeholder="CMO, CFO, CEO..." autoComplete="organization-title" {...register("ruolo")} data-testid="contact-ruolo" />
      </div>
      <div>
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Email *
        </label>
        <input
          type="email"
          className="input-os"
          placeholder="mario@azienda.com"
          autoComplete="email"
          {...register("email")}
          data-testid="contact-email"
        />
        {errors.email && <p className="text-rosso-enzo text-sm mt-1">{errors.email.message}</p>}
      </div>
      <div>
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Telefono
        </label>
        <input className="input-os" placeholder="+39..." autoComplete="tel" inputMode="tel" {...register("telefono")} data-testid="contact-telefono" />
      </div>
      <div>
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Tipo di esigenza
        </label>
        <select className="input-os" {...register("esigenza")} data-testid="contact-esigenza">
          <option value="">Seleziona…</option>
          {ESIGENZE.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Messaggio *
        </label>
        <textarea
          className="input-os min-h-[140px]"
          placeholder="Raccontaci i tuoi obiettivi…"
          {...register("messaggio")}
          data-testid="contact-messaggio"
        />
        {errors.messaggio && (
          <p className="text-rosso-enzo text-sm mt-1">{errors.messaggio.message}</p>
        )}
      </div>
      <div className="md:col-span-2 pt-3">
        <button
          type="submit"
          disabled={submitting}
          className="btn-os btn-primary"
          data-testid="contact-submit"
        >
          <span>{submitting ? "Invio in corso…" : "Invia messaggio"}</span>
          <span className="arrow" aria-hidden="true">→</span>
        </button>
      </div>

      {/* Stato success */}
      {status === "success" && (
        <div
          className="md:col-span-2 mt-2 border border-lime bg-lime/10 px-5 py-4 text-sm"
          data-testid="contact-success"
          role="status"
        >
          <strong className="font-condensed uppercase tracking-widest text-[13px] block mb-1">
            ✓ Richiesta ricevuta
          </strong>
          Grazie, abbiamo ricevuto la tua richiesta. Ti rispondiamo al più presto, in genere entro 24 ore lavorative.
        </div>
      )}

      {/* Stato error */}
      {status === "error" && (
        <div
          className="md:col-span-2 mt-2 border border-rosso-enzo bg-rosso-enzo/10 px-5 py-4 text-sm"
          data-testid="contact-error"
          role="alert"
        >
          <strong className="font-condensed uppercase tracking-widest text-[13px] block mb-1">
            ✕ Errore
          </strong>
          {errorMsg}
        </div>
      )}
    </form>
  );
};

export default ContactForm;
